import { ServiceUnavailableComponent } from './pages/service-unavailable/service-unavailable.component';
import { Routes, UrlMatchResult, UrlSegment } from '@angular/router';
import { APPLE_STORE_PAGES_ENABLED, authGuard, HREF } from '@core';
import { SHOP_NAME } from '@di-tokens';
import { LoginComponent } from '@login';
import { LogoutComponent } from '@logout';
import { INFO_PAGE_HELPFULL_LINKS, PRODUCT_LIST_CARD_PARAMS } from '@shared';
import { CHECKOUT_ROUTES_BRANCH } from './pages/checkout/checkout.routes';
import {
  FAQHelpfullLinksFabric,
  privacyPolicyHelpfullLinksFabric,
  ReturnsAndRefundsHelpfullLinksFabric,
  shippingAndDeliveryHelpfullLinksFabric,
  termsAndConditionsHelpfullLinksFabric,
} from './pages/info/constants/helpfull-links';
import { PagesComponent } from './pages/pages.component';
import { HomeComponent } from './pages/home/home.component';

export const routes: Routes = [
  {
    path: HREF.NOT_FOUND,
    component: LoginComponent,
    pathMatch: 'full',
  },
  {
    path: HREF.NOT_AVAILABLE,
    component: ServiceUnavailableComponent,
    pathMatch: 'full',
  },
  {
    path: HREF.SIGN_IN_FAILED,
    loadComponent: () => import('./pages/sign-in-failed/sign-in-failed.component'),
    pathMatch: 'full',
  },
  {
    path: HREF.MISSING_INFORMATION,
    loadComponent: () => import('./pages/not-allowed-purchase/not-allowed-purchase.component'),
    pathMatch: 'full',
  },
  {
    path: '',
    component: PagesComponent,
    canActivate: [authGuard],
    runGuardsAndResolvers: 'always',
    providers: [
      {
        provide: SHOP_NAME,
        useValue: 'Rymax',
      },
    ],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: HREF.HOME,
      },
      {
        path: HREF.HOME,
        component: HomeComponent,
        pathMatch: 'full',
      },
      {
        path: HREF.LOGOUT,
        component: LogoutComponent,
      },
      {
        path: HREF.ACCOUNT,
        loadComponent: () => import('./pages/account/user-info/user-info.component'),
        data: { breadcrumb: 'Dashboard' },
      },
      // RCP-442: order history disabled
      // {
      //   path: HREF.ORDERS,
      //   loadComponent: () => import('./pages/account/orders-list/orders-list.component'),
      //   data: { breadcrumb: 'Orders' },
      // },
      // RCP-441: wishlist disabled
      // {
      //   path: HREF.WISHLIST,
      //   loadComponent: () => import('./pages/wishlist/wishlist.component'),
      // },
      {
        path: HREF.CART,
        loadComponent: () => import('./pages/cart/cart.component'),
      },
      CHECKOUT_ROUTES_BRANCH,
      {
        path: HREF.BRAND_PRODUCTS_withParam,
        loadComponent: () => import('./pages/brand-store/brand-store.component'),
        providers: [{ provide: PRODUCT_LIST_CARD_PARAMS, useValue: { isDisplayBrandNameLabel: false } }],
      },
      APPLE_STORE_PAGES_ENABLED
        ? {
            matcher: appleCategoryMatcher,
            loadComponent: () => import('./pages/category-apple/category-apple.component'),
          }
        : { matcher: appleCategoryMatcher, redirectTo: `/${HREF.HOME}` },
      {
        matcher: categoryIdMatcher,
        loadComponent: () => import('./pages/category-products/category-products.component'),
        pathMatch: 'full',
      },
      {
        path: HREF.BRANDS_DIRECTORY,
        loadComponent: () => import('./pages/brands/brands.component'),
      },
      {
        path: HREF.SALE,
        loadComponent: () => import('./pages/sale-products/sale-products.component'),
      },
      {
        path: HREF.PRODUCT_DETAILS,
        loadComponent: () => import('./pages/products/product.component').then((m) => m.ProductComponent),
      },
      {
        path: `${HREF.SEARCH_RESULTS}/:query`,
        loadComponent: () => import('./pages/search-results/search-results.component'),
      },
      {
        path: HREF.INFO,
        children: [
          {
            path: HREF.FAQ,
            loadComponent: () => import('./pages/info/faq/faq.component').then((m) => m.FaqComponent),
            providers: [
              {
                provide: INFO_PAGE_HELPFULL_LINKS,
                useFactory: () => FAQHelpfullLinksFabric(),
              },
            ],
          },
          {
            path: HREF.SHIPPING_AND_DELIVERY,
            loadChildren: () => import('./pages/info/shippingpolicy/shippingpolicy.routes'),
            providers: [
              {
                provide: INFO_PAGE_HELPFULL_LINKS,
                useFactory: (shopName) => shippingAndDeliveryHelpfullLinksFabric(shopName),
                deps: [SHOP_NAME],
              },
            ],
          },
          {
            path: HREF.RETURNS_AND_REFUNDS,
            loadComponent: () =>
              import('./pages/info/return-and-refund/return-and-refund.component').then(
                (m) => m.ReturnAndRefundComponent,
              ),
            providers: [
              {
                provide: INFO_PAGE_HELPFULL_LINKS,
                useFactory: (shopName) => ReturnsAndRefundsHelpfullLinksFabric(shopName),
                deps: [SHOP_NAME],
              },
            ],
          },
          {
            path: HREF.TERMS_AND_CONDITIONS,
            loadComponent: () =>
              import('./pages/info/terms-and-conditions/terms-and-conditions.component').then(
                (m) => m.TermsAndConditionsComponent,
              ),
            providers: [
              {
                provide: INFO_PAGE_HELPFULL_LINKS,
                useFactory: (shopName) => termsAndConditionsHelpfullLinksFabric(shopName),
                deps: [SHOP_NAME],
              },
            ],
          },
          {
            path: HREF.TERMS_AND_CONDITIONS_APPLE,
            redirectTo: HREF.TERMS_AND_CONDITIONS,
          },
          {
            path: HREF.PRIVACY_POLICY,
            loadComponent: () =>
              import('./pages/info/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
            providers: [
              {
                provide: INFO_PAGE_HELPFULL_LINKS,
                useFactory: (shopName) => privacyPolicyHelpfullLinksFabric(shopName),
                deps: [SHOP_NAME],
              },
            ],
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: HREF.NOT_FOUND },
];

export function categoryIdMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  // Check if the URL has exactly three segments: 'marketplace', 'category', and ':id'
  if (segments.length === 3 && segments[0].path === 'marketplace' && segments[1].path === 'category') {
    const id = segments[2].path;
    // Validate that 'id' consists only of digits
    const isValidId = /^[0-9]+$/.test(id);
    if (isValidId) {
      return {
        consumed: segments,
        posParams: {
          id: new UrlSegment(id, {}),
        },
      };
    }
  }
  return null; // No match; continue to the next route
}

export function marketplaceIdMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  // Check if the URL has exactly two segments: 'marketplace' and ':id'
  if (segments.length === 2 && segments[0].path === 'marketplace') {
    const id = segments[1].path;
    // Validate that 'id' consists only of digits
    const isValidId = /^[0-9]+$/.test(id);
    if (isValidId) {
      return {
        consumed: segments,
        posParams: {
          id: new UrlSegment(id, {}),
        },
      };
    }
  }
  return null; // No match; continue to the next route
}
export function appleCategoryMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  // Check if the URL has exactly three segments: 'marketplace', 'category-apple', and ':id'
  if (segments.length === 3 && segments[0].path === 'marketplace' && segments[1].path === 'category-apple') {
    const id = segments[2].path;
    return {
      consumed: segments,
      posParams: {
        id: new UrlSegment(id, {}),
      },
    };
  }
  return null;
}
